import { useRef, useState } from 'react';
import { Badge, Button, InlineError, Input, Select, Skeleton } from '../../components/ui';
import { DriveReconnectNotice } from '../auth/ReconnectDrive';
import { useAuth } from '../auth/AuthProvider';
import { useAttachLink, useDeleteFile, useTaskFiles, useUploadFile } from './hooks';
import { fileErrorMessage } from './queries';
import {
  drivePreviewUrl,
  FILE_CATEGORY_LABELS,
  FILE_REVIEW_STATUS_LABELS,
  formatFileSize,
  groupIntoAssets,
  isImageMime,
  isPdfMime,
} from './types';
import type { SelectOption } from '../../components/ui';
import type { FileCategory, FileWithUploader } from './types';

/**
 * The Attachments section of the task details panel (handoff 27 and 30):
 * upload a file to Drive, paste a Drive or external link, preview images and
 * PDFs inline, upload a new version of an existing asset, and read the
 * asset's version history. The latest version appears prominently; older
 * versions sit behind a History toggle. Deletion follows handoff 29, and
 * removing a latest version reinstates the one before it (database-owned).
 *
 * Nothing here decides permission. canEdit and canManage hide controls as a
 * convenience; Row Level Security refuses anything the caller may not do.
 */

const CATEGORY_OPTIONS: SelectOption[] = (
  Object.entries(FILE_CATEGORY_LABELS) as [FileCategory, string][]
)
  .filter(([key]) => key !== 'supplier_review') // arrives with Phase 6
  .map(([value, label]) => ({ value, label }));

/** Drive previews render for Drive files whose type previews well. */
function canPreview(file: FileWithUploader): boolean {
  if (file.drive_file_id === null) {
    return false;
  }
  // A pasted Drive link has no recorded type; Drive's preview page copes
  // with nearly anything, so offer it.
  return file.mime_type === null || isImageMime(file.mime_type) || isPdfMime(file.mime_type);
}

/** When the version arrived. The date is enough; files are not a chat. */
function formatVersionDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function uploaderLabel(file: FileWithUploader): string {
  return file.uploader_name === '' ? 'Someone' : file.uploader_name;
}

/** An older version: readable facts only, opened in Drive if needed. */
function VersionHistoryItem({ version }: { version: FileWithUploader }) {
  return (
    <li className="file-version-row">
      <span className="file-version-number">{`V${String(version.version_number)}`}</span>
      <a className="file-name" href={version.url} target="_blank" rel="noreferrer">
        {version.name}
      </a>
      <span className="file-meta">
        {uploaderLabel(version)}
        {` · ${formatVersionDate(version.created_at)}`}
        {version.review_status === 'none'
          ? ''
          : ` · ${FILE_REVIEW_STATUS_LABELS[version.review_status]}`}
      </span>
      {version.version_note === null || version.version_note === '' ? null : (
        <span className="file-version-note">{version.version_note}</span>
      )}
    </li>
  );
}

function FileRowItem({
  file,
  history,
  canEdit,
  canManage,
  currentUserId,
  onDelete,
  onUploadVersion,
  deletePending,
  uploadPending,
}: {
  file: FileWithUploader;
  history: FileWithUploader[];
  canEdit: boolean;
  canManage: boolean;
  currentUserId: string;
  onDelete: (fileId: string) => void;
  onUploadVersion: (file: FileWithUploader, picked: File, note: string, done: () => void) => void;
  deletePending: boolean;
  uploadPending: boolean;
}) {
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [versionFormOpen, setVersionFormOpen] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const versionInputRef = useRef<HTMLInputElement | null>(null);

  const size = formatFileSize(file.size_bytes);
  const versioned = history.length > 0 || file.version_number > 1;
  // New versions join the chain of the current latest. A legacy row without
  // a chain (uploaded before versioning existed) cannot grow one — the
  // group is frozen after insert — so the offer is not made.
  const mayVersion = canEdit && file.asset_group_id !== null && file.is_latest_version;
  // Handoff 29: uploaders remove their own unapproved files; managers
  // remove anything. The database enforces this; the button just matches it.
  const mayDelete =
    canManage ||
    (canEdit && file.uploaded_by === currentUserId && file.review_status !== 'approved');

  function closeVersionForm() {
    setVersionFormOpen(false);
    setVersionNote('');
  }

  return (
    <li className="file-row">
      <div className="file-row-main">
        <a className="file-name" href={file.url} target="_blank" rel="noreferrer">
          {file.name}
        </a>
        <span className="file-meta">
          {versioned ? `V${String(file.version_number)} · ` : ''}
          {FILE_CATEGORY_LABELS[file.category]}
          {size === '' ? '' : ` · ${size}`}
          {` · ${uploaderLabel(file)}`}
          {file.source === 'external_link' ? ' · external link' : ''}
        </span>
        {file.review_status === 'approved' ? <Badge tone="success">Approved</Badge> : null}
      </div>

      <div className="file-row-actions">
        {canPreview(file) ? (
          <Button variant="ghost" size="sm" onClick={() => setPreviewing((value) => !value)}>
            {previewing ? 'Hide preview' : 'Preview'}
          </Button>
        ) : null}
        {mayVersion ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`New version of ${file.name}`}
            onClick={() => {
              setVersionFormOpen((value) => !value);
              setVersionNote('');
            }}
          >
            {versionFormOpen ? 'Cancel version' : 'New version'}
          </Button>
        ) : null}
        {history.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Version history of ${file.name}`}
            onClick={() => setShowHistory((value) => !value)}
          >
            {showHistory ? 'Hide history' : `History (${String(history.length)})`}
          </Button>
        ) : null}
        {mayDelete ? (
          confirming ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Keep
              </Button>
              <Button
                variant="danger"
                size="sm"
                disabled={deletePending}
                onClick={() => {
                  setConfirming(false);
                  onDelete(file.id);
                }}
              >
                Confirm delete
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Delete file ${file.name}`}
              onClick={() => setConfirming(true)}
            >
              Delete
            </Button>
          )
        ) : null}
      </div>

      {file.version_note === null || file.version_note === '' ? null : (
        <span className="file-version-note">{file.version_note}</span>
      )}

      {versionFormOpen ? (
        <div className="file-version-form">
          <Input
            label="Version note (optional)"
            value={versionNote}
            placeholder="What changed?"
            onChange={(event) => setVersionNote(event.target.value)}
          />
          <input
            ref={versionInputRef}
            type="file"
            className="file-input-hidden"
            aria-label={`Choose the new version of ${file.name}`}
            onChange={(event) => {
              const picked = event.target.files?.[0];
              if (picked) {
                onUploadVersion(file, picked, versionNote, closeVersionForm);
              }
              // Allow picking the same file again after a failure.
              if (versionInputRef.current) {
                versionInputRef.current.value = '';
              }
            }}
          />
          <Button
            size="sm"
            disabled={uploadPending}
            onClick={() => versionInputRef.current?.click()}
          >
            {uploadPending ? 'Uploading…' : 'Choose file'}
          </Button>
        </div>
      ) : null}

      {showHistory && history.length > 0 ? (
        <ul className="file-version-history" aria-label={`Older versions of ${file.name}`}>
          {history.map((version) => (
            <VersionHistoryItem key={version.id} version={version} />
          ))}
        </ul>
      ) : null}

      {previewing && file.drive_file_id !== null ? (
        <iframe
          className="file-preview"
          src={drivePreviewUrl(file.drive_file_id)}
          title={`Preview of ${file.name}`}
          allow="autoplay"
        />
      ) : null}
    </li>
  );
}

export function FilesSection({
  taskId,
  projectId,
  projectName,
  canEdit,
  canManage,
  heading = 'Attachments',
  headingClassName = 'task-panel-heading',
}: {
  taskId: string;
  projectId: string;
  /** Names the Drive subfolder uploads land in. */
  projectName: string;
  canEdit: boolean;
  canManage: boolean;
  /** Empty when the container already names the section (the task panel's fold). */
  heading?: string;
  /** Lets a host surface match its own heading hierarchy without changing the shared default. */
  headingClassName?: string;
}) {
  const { access } = useAuth();
  const currentUserId = access.status === 'approved' ? access.profile.id : '';

  const [category, setCategory] = useState<FileCategory>('task_asset');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const files = useTaskFiles(taskId, projectId);
  const uploadFile = useUploadFile(taskId, projectId, projectName);
  const attachLink = useAttachLink(taskId, projectId);
  const deleteFile = useDeleteFile(taskId, projectId);

  function report(cause: unknown) {
    setError(fileErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function onFilePicked(picked: File | undefined) {
    if (!picked) {
      return;
    }
    setError('');
    uploadFile.mutate(
      { file: picked, category },
      {
        onError: report,
        onSettled: () => {
          // Allow picking the same file again after a failure.
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
      },
    );
  }

  /** A new version keeps its asset's category and joins its chain. */
  function onUploadVersion(file: FileWithUploader, picked: File, note: string, done: () => void) {
    if (file.asset_group_id === null) {
      return;
    }
    setError('');
    const trimmed = note.trim();
    uploadFile.mutate(
      {
        file: picked,
        category: file.category,
        assetGroupId: file.asset_group_id,
        ...(trimmed === '' ? {} : { versionNote: trimmed }),
      },
      { onSuccess: done, onError: report },
    );
  }

  function submitLink() {
    const url = linkUrl.trim();
    const name = linkName.trim();
    if (url === '' || name === '') {
      setError('Give the link an address and a name.');
      return;
    }
    setError('');
    attachLink.mutate(
      { url, name, category },
      {
        onSuccess: () => {
          setLinkUrl('');
          setLinkName('');
          setLinkOpen(false);
        },
        onError: report,
      },
    );
  }

  function remove(fileId: string) {
    setError('');
    deleteFile.mutate(fileId, { onError: report });
  }

  const assets = groupIntoAssets(files.data ?? []);

  return (
    <section className="files-section" aria-label="Attachments">
      {heading === '' ? null : <h3 className={headingClassName}>{heading}</h3>}

      {error === '' ? null : (
        <>
          <InlineError>{error}</InlineError>
          {/* An expired Google grant is the one upload failure the person can
              actually fix, so the fix sits next to the message. */}
          {error.toLowerCase().includes('drive') ? <DriveReconnectNotice message="" /> : null}
        </>
      )}

      {files.isPending ? (
        <Skeleton lines={2} label="Loading attachments" />
      ) : files.isError ? (
        <p className="task-panel-text">Attachments could not be loaded.</p>
      ) : assets.length === 0 ? (
        <p className="task-panel-text">No attachments yet</p>
      ) : (
        <ul className="file-list">
          {assets.map((asset) => (
            <FileRowItem
              key={asset.latest.id}
              file={asset.latest}
              history={asset.history}
              canEdit={canEdit}
              canManage={canManage}
              currentUserId={currentUserId}
              onDelete={remove}
              onUploadVersion={onUploadVersion}
              deletePending={deleteFile.isPending}
              uploadPending={uploadFile.isPending}
            />
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="file-controls">
          <Select
            label="Category"
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={(event) => setCategory(event.target.value as FileCategory)}
          />
          <input
            ref={fileInputRef}
            type="file"
            className="file-input-hidden"
            aria-label="Choose a file to upload"
            onChange={(event) => onFilePicked(event.target.files?.[0])}
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploadFile.isPending}>
            {uploadFile.isPending ? 'Uploading…' : 'Upload file'}
          </Button>
          <Button variant="secondary" onClick={() => setLinkOpen((value) => !value)}>
            {linkOpen ? 'Cancel link' : 'Paste a link'}
          </Button>
        </div>
      ) : null}

      {canEdit && linkOpen ? (
        <div className="file-link-form">
          <Input
            label="Link address"
            value={linkUrl}
            placeholder="https://drive.google.com/… or any web address"
            onChange={(event) => setLinkUrl(event.target.value)}
          />
          <Input
            label="Name"
            value={linkName}
            placeholder="What is this?"
            onChange={(event) => setLinkName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitLink();
              }
            }}
          />
          <Button onClick={submitLink} disabled={attachLink.isPending}>
            {attachLink.isPending ? 'Attaching…' : 'Attach link'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
